import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader } from "../../components/bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Save, Trash2 } from "lucide-react";

function sigmaFromDpmo(dpmo) {
  if (dpmo <= 3.4) return 6.0;
  if (dpmo >= 933193) return 0;
  const v = 29.37 - 2.221 * Math.log(dpmo);
  return v > 0 ? 0.8406 + Math.sqrt(v) : 0;
}

function rating(sigma) {
  if (sigma >= 5) return { text: "World class", cls: "text-emerald-600" };
  if (sigma >= 4) return { text: "Good — above industry average", cls: "text-emerald-600" };
  if (sigma >= 3) return { text: "Industry average — improvement opportunity", cls: "text-amber-600" };
  if (sigma >= 2) return { text: "Below average — significant defects", cls: "text-red-600" };
  return { text: "Non-competitive — urgent improvement required", cls: "text-red-600" };
}

export default function CapabilityPage() {
  const { projectId } = useParams();
  const [studies, setStudies] = useState([]);
  const [form, setForm] = useState({ name: "", process: "", units: "", opportunities: "", defects: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/projects/${projectId}/artifacts/capability_studies`).then((r) => setStudies(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [projectId]);

  const units = parseFloat(form.units) || 0;
  const opps = parseFloat(form.opportunities) || 0;
  const defects = parseFloat(form.defects) || 0;
  const totalOpps = units * opps;
  const valid = units > 0 && opps > 0 && defects >= 0 && defects <= totalOpps;
  const dpo = valid ? defects / totalOpps : 0;
  const dpmo = dpo * 1e6;
  const yieldPct = (1 - dpo) * 100;
  const sigma = valid ? sigmaFromDpmo(dpmo) : 0;
  const r = rating(sigma);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/artifacts/capability_studies`, {
        name: form.name || "Capability study", process: form.process,
        units, opportunities: opps, defects,
        dpmo: Math.round(dpmo), yieldPct: Number(yieldPct.toFixed(2)), sigmaLevel: Number(sigma.toFixed(2)),
      });
      toast.success("Study saved");
      setForm({ name: "", process: "", units: "", opportunities: "", defects: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const remove = async (id) => {
    try { await api.delete(`/projects/${projectId}/artifacts/capability_studies/${id}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="capability-page">
      <PageHeader overline="Six Sigma Toolkit · DPMO method" title="Process Capability Calculator" />
      <div className="grid lg:grid-cols-2 gap-6 mt-4">
        <div className="border border-border bg-card p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Study name</Label><Input data-testid="cap-name-input" value={form.name} onChange={set("name")} placeholder="e.g. Invoice accuracy Q2" /></div>
            <div><Label>Process / CTQ</Label><Input data-testid="cap-process-input" value={form.process} onChange={set("process")} placeholder="e.g. Invoice line entry" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Units inspected</Label><Input data-testid="cap-units-input" type="number" min="1" value={form.units} onChange={set("units")} /></div>
            <div><Label>Opportunities / unit</Label><Input data-testid="cap-opps-input" type="number" min="1" value={form.opportunities} onChange={set("opportunities")} /></div>
            <div><Label>Defects found</Label><Input data-testid="cap-defects-input" type="number" min="0" value={form.defects} onChange={set("defects")} /></div>
          </div>
          <p className="text-xs text-muted-foreground">DPMO = defects ÷ (units × opportunities) × 1,000,000. Sigma level includes the conventional 1.5σ shift.</p>
          <Button data-testid="cap-save-btn" className="rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]" onClick={save} disabled={!valid || busy}>
            <Save className="h-4 w-4 mr-1.5" /> Save study
          </Button>
          {!valid && (units > 0 || opps > 0 || defects > 0) && defects > totalOpps &&
            <div className="text-xs text-red-600" data-testid="cap-validation-error">Defects cannot exceed total opportunities ({totalOpps.toLocaleString()}).</div>}
        </div>

        <div className="border border-border bg-card p-5">
          <div className="overline-label mb-3">Results</div>
          <div className="grid grid-cols-2 gap-4">
            <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total opportunities</div>
              <div data-testid="cap-total-opps" className="font-display font-black text-2xl tracking-tighter">{valid ? totalOpps.toLocaleString() : "—"}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">DPMO</div>
              <div data-testid="cap-dpmo" className="font-display font-black text-2xl tracking-tighter">{valid ? Math.round(dpmo).toLocaleString() : "—"}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Process yield</div>
              <div data-testid="cap-yield" className="font-display font-black text-2xl tracking-tighter">{valid ? `${yieldPct.toFixed(2)}%` : "—"}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Sigma level</div>
              <div data-testid="cap-sigma" className="font-display font-black text-2xl tracking-tighter text-[#1E6FE8]">{valid ? `${sigma.toFixed(2)}σ` : "—"}</div></div>
          </div>
          {valid && <div data-testid="cap-rating" className={`mt-4 text-sm font-semibold ${r.cls}`}>{r.text}</div>}
          <div className="mt-4 text-[11px] text-muted-foreground border-t border-border pt-3">
            Benchmarks: 6σ = 3.4 DPMO · 5σ = 233 · 4σ = 6,210 · 3σ = 66,807 · 2σ = 308,537
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="overline-label mb-2">Saved studies</div>
        <table className="w-full text-sm border border-border bg-card">
          <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
            <th className="px-3 py-2">Study</th><th className="px-3 py-2">Process</th><th className="px-3 py-2 text-right">Units</th>
            <th className="px-3 py-2 text-right">Opps/unit</th><th className="px-3 py-2 text-right">Defects</th>
            <th className="px-3 py-2 text-right">DPMO</th><th className="px-3 py-2 text-right">Yield</th>
            <th className="px-3 py-2 text-right">Sigma</th><th className="px-3 py-2" /></tr></thead>
          <tbody>
            {studies.map((s) => (
              <tr key={s.id} data-testid="cap-study-row" className="border-b border-border last:border-0">
                <td className="px-3 py-2 font-medium">{s.name}</td><td className="px-3 py-2">{s.process}</td>
                <td className="px-3 py-2 text-right">{s.units?.toLocaleString()}</td>
                <td className="px-3 py-2 text-right">{s.opportunities}</td>
                <td className="px-3 py-2 text-right">{s.defects?.toLocaleString()}</td>
                <td className="px-3 py-2 text-right">{s.dpmo?.toLocaleString()}</td>
                <td className="px-3 py-2 text-right">{s.yieldPct}%</td>
                <td className="px-3 py-2 text-right font-semibold text-[#1E6FE8]">{s.sigmaLevel}σ</td>
                <td className="px-3 py-2 text-right"><button data-testid="cap-delete-btn" onClick={() => remove(s.id)} className="text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {studies.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">No studies saved yet — run a calculation and save it.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
